import React, {Component, ReactNode} from 'react'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {AssocOfCondominium} from '../assocDirector/model/AssocOfCondominium'
import {Nullable} from '../../app/TypeHelpers'
import {OptionalChip, StyledTableCaption, TableRowName} from '../../styledComponents/adminPage/Table'
import {
    Box,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableRow
} from '@mui/material'
import {SelectChangeEvent} from '@mui/material/Select'
import PPTextField from '../shared/forms/PPTextField'
import PPCheckboxGroup from '../shared/forms/PPCheckboxGroup'
import {
    FullWidthLeftColumnFlexContainer,
    FullWidthLeftRowFlexContainer,
    FullWidthRowFlexContainer,
    LeftColumnFlexContainer,
    LeftRowFlexContainer,
    LeftRowFlexWrapContainer
} from '../../styledComponents/FlexComponents'
import PPDatePicker from '../shared/forms/PPDatePicker'
import PPSelect from '../shared/forms/PPSelect'
import {addEmptyData, NoSelectedValue} from '../../componentHelper/selectHelper'
import {AVAILABLE_OR_NOT, HOURS, RESERVATION_TIME_UNIT_MINUTES, RESOLUTION_TYPE} from '../../CodeMaster'
import {Colors, FontSizeInPixel} from '../../styledComponents/Constants'
import {PageMainArea} from '../shared/hoc/withAdminPageTemplate'
import styled from 'styled-components'
import {AddCircle, ArrowDownward, ArrowUpward} from '@mui/icons-material'
import {CustomStyledLink, DeleteLink, StyledLink} from '../../styledComponents/adminPage/Links'
import {createUUID} from '../../stringHelper/stringHelpers'
import {Messages} from '../../Messages'
import {HKConstants} from '../../HKConstants'
import PPErrorLabel from '../shared/forms/PPErrorLabel'
import {AdminDefaultButton} from '../../styledComponents/adminPage/Buttons'
import {RequestItemError} from '../../network/response/RequestItemError'
import {getFileExtension} from '../../__NoAutomatedTests/common/util/Natutil'
import {getLinkIconComponent} from '../assocDirector/AssocDirectorCommon'
import {AttachmentFileStyle} from '../assocDirector/AdminMeetingListPage'
import {NatFileRepo} from '../../__NoAutomatedTests/components/files/repository/NatFileRepo'
import {AsyncCallFunctionType} from '../shared/hoc/withAsyncCommunication'
import {Divider} from '../../__NoAutomatedTests/components/AmountFix/common/BillingDataCommon'
import {GeneralMeetingFile} from './AdminGeneralMeetingRepo'
import {formatFileSize} from '../../formatters/formatFileSize'

export interface GeneralMeetingFormDataSet {
    generalMeetingName: string
    selectedAssocIdList: number[]
    generalMeetingDate: Nullable<Date>
    startHour: string
    startMinute: string
    votingPreferenceDeadline: Nullable<Date>
    questionAvailability: string
    questionDeadline: Nullable<Date>
    generalMeetingFileList: GeneralMeetingFile[]
    proposalFormDataSetList: ProposalByAssocFormDataSet[]
    noticeContent: string
    releaseDate: Nullable<Date>
}

export interface ProposalByAssocFormDataSet {
    assocId: number,
    proposalDetailInfoFormDataSetList: ProposalDetailInfoFormDataSet[]
}

export interface ProposalDetailInfoFormDataSet {
    proposalUID: string
    proposalNumber: number
    resolutionType: Nullable<string>
    proposalName: string
}

export function canSaveAsDraftGeneralMeeting(generalMeetingFormDataSet: GeneralMeetingFormDataSet): boolean {
    const hourEmpty = generalMeetingFormDataSet.startHour === NoSelectedValue
    const minuteEmpty = generalMeetingFormDataSet.startMinute === NoSelectedValue
    return hourEmpty === minuteEmpty
}

export function canRegisterGeneralMeeting(generalMeetingFormDataSet: GeneralMeetingFormDataSet): boolean {
    function checkRequiredItemsForProposal(): boolean {
        const proposalFormDataList = generalMeetingFormDataSet.proposalFormDataSetList
            .filter(proposalByAssocFormDataSet =>
                generalMeetingFormDataSet.selectedAssocIdList.includes(proposalByAssocFormDataSet.assocId))

        if (proposalFormDataList.length === 0) return false

        for (const proposalFormData of proposalFormDataList) {
            const proposalDetailInfoFormDataList = proposalFormData.proposalDetailInfoFormDataSetList
            if (proposalDetailInfoFormDataList.length === 0) return false
            if (proposalDetailInfoFormDataList[0].resolutionType === null) return false
            if (proposalDetailInfoFormDataList[0].proposalName === '') return false
        }

        return true
    }

    return !(generalMeetingFormDataSet.generalMeetingName === ''
        || generalMeetingFormDataSet.selectedAssocIdList.length === 0
        || generalMeetingFormDataSet.generalMeetingDate === null
        || generalMeetingFormDataSet.startHour === NoSelectedValue
        || generalMeetingFormDataSet.startMinute === NoSelectedValue
        || generalMeetingFormDataSet.votingPreferenceDeadline === null
        || (generalMeetingFormDataSet.questionAvailability === AVAILABLE_OR_NOT.AVAILABLE.value
            && generalMeetingFormDataSet.questionDeadline === null)
        || generalMeetingFormDataSet.generalMeetingFileList.length === 0
        || (!checkRequiredItemsForProposal())
        || generalMeetingFormDataSet.releaseDate === null)
}

interface Props {
    condominiumCd: string
    assocList: AssocOfCondominium[]
    generalMeetingFormDataSet: GeneralMeetingFormDataSet
    currentDate: Date
    eventEmitter: EventEmitterExAdmin
    onChangeGeneralMeetingFormDataSet: (value: GeneralMeetingFormDataSet) => void
    onDeleteExistingFile?: (fileNumber: number) => void
    natFileRepo: NatFileRepo
    asyncCallFunction: AsyncCallFunctionType
    isAfterRelease: boolean
}

interface State {
    generalMeetingName: string
    selectedAssocIdList: number[]
    generalMeetingDate: Nullable<Date>
    startHour: Nullable<string>
    startMinute: Nullable<string>
    votingPreferenceDeadline: Nullable<Date>
    questionAvailability: Nullable<string>
    questionDeadline: Nullable<Date>
    generalMeetingFile: GeneralMeetingFile
    noticeContent: Nullable<string>
    releaseDate: Nullable<Date>
}

export class AdminGeneralMeetingInputForm extends Component<Props, State> {
    constructor(props: Readonly<Props>) {
        super(props)

        this.callOnChangeGeneralMeetingFormDataSet = this.callOnChangeGeneralMeetingFormDataSet.bind(this)
        this.changeGeneralMeetingName = this.changeGeneralMeetingName.bind(this)
        this.changeAssoc = this.changeAssoc.bind(this)
        this.changeGeneralMeetingDate = this.changeGeneralMeetingDate.bind(this)
        this.changeStartHour = this.changeStartHour.bind(this)
        this.changeStartMinute = this.changeStartMinute.bind(this)
        this.changeVotingPreferenceDeadline = this.changeVotingPreferenceDeadline.bind(this)
        this.changeQuestionAvailability = this.changeQuestionAvailability.bind(this)
        this.changeQuestionDeadline = this.changeQuestionDeadline.bind(this)
        this.changeResolutionType = this.changeResolutionType.bind(this)
        this.changeProposalName = this.changeProposalName.bind(this)
        this.handleUpProposal = this.handleUpProposal.bind(this)
        this.handleDownProposal = this.handleDownProposal.bind(this)
        this.handleAddProposal = this.handleAddProposal.bind(this)
        this.handleDeleteProposal = this.handleDeleteProposal.bind(this)
        this.changeNoticeContent = this.changeNoticeContent.bind(this)
        this.changeReleaseDate = this.changeReleaseDate.bind(this)
    }

    static checkFileExtension(file: File): string[] {
        const extension = getFileExtension(file.name).toLowerCase()
        if (extension !== 'pdf') {
            return [Messages.EOPCA0005.toString()]
        }
        return []
    }

    static checkFileSize(file: File): string[] {
        if (file.size === 0) {
            return [Messages.EGMTA0005.toString()]
        }
        const maxFileSizeBytes = HKConstants.VMN.GMT.MAX_FILE_SIZE * 1024 * 1024
        if (file.size > maxFileSizeBytes) {
            return [Messages.EGMTA0003.toString(String(HKConstants.VMN.GMT.MAX_FILE_SIZE))]
        }
        return []
    }

    static checkTotalFileSize(file: File, currentFileList: GeneralMeetingFile[]): string[] {
        const maxTotalFileSizeBytes = HKConstants.VMN.GMT.MAX_TOTAL_FILE_SIZE * 1024 * 1024
        const currentTotalSize = currentFileList.reduce(
            (totalSize, fileDataSet) => totalSize + fileDataSet.fileSize, 0
        )
        if (currentTotalSize + file.size > maxTotalFileSizeBytes) {
            return [Messages.EGMTA0004.toString(String(HKConstants.VMN.GMT.MAX_TOTAL_FILE_SIZE))]
        }
        return []
    }

    async checkFileName(file: File): Promise<string[]> {
        const result = await this.props.asyncCallFunction(
            this.props.natFileRepo.validateUploadFile.bind(
                this.props.natFileRepo,
                {fileName: file.name}
            ),
            () => {
                // バリデーションエラーは独自にハンドリングするため、broadcastしない
            },
        )
        if (result === null) {
            return [Messages.EGMTA0006.toString()]
        }
        return []
    }

    componentDidUpdate(prevProps: Props): void {
        if (prevProps.assocList !== this.props.assocList) {
            if (this.props.assocList.length === 1) {
                this.changeAssoc(this.props.assocList[0].assocName, true)
            }
        }
    }

    callOnChangeGeneralMeetingFormDataSet(updatePartialObject: Partial<GeneralMeetingFormDataSet>): void {
        if (this.props.onChangeGeneralMeetingFormDataSet) {
            this.props.onChangeGeneralMeetingFormDataSet({
                ...this.props.generalMeetingFormDataSet,
                ...updatePartialObject,
            })
        }
    }

    changeGeneralMeetingName(event: React.ChangeEvent<HTMLInputElement>): void {
        this.callOnChangeGeneralMeetingFormDataSet({generalMeetingName: event.target.value})
    }

    changeAssoc(label: string, checked: boolean): void {
        const changedAssoc = this.props.assocList.find(assoc => assoc.assocName === label)
        if (!changedAssoc) return
        const changedAssocId = changedAssoc.assocId

        const currentList = this.props.generalMeetingFormDataSet.selectedAssocIdList
        const exists: boolean = currentList.includes(changedAssocId)

        const proposalList = [...this.props.generalMeetingFormDataSet.proposalFormDataSetList]
        let nextList: number[] = []

        if (checked) {
            if (!exists) {
                nextList = [...currentList, changedAssocId]
                if (!proposalList.find(proposal => proposal.assocId === changedAssocId)) {
                    proposalList.push({
                        assocId: changedAssocId,
                        proposalDetailInfoFormDataSetList: Array.from({length: 3}, (_, i) => ({
                            proposalUID: createUUID(),
                            proposalNumber: i + 1,
                            resolutionType: null,
                            proposalName: ''
                        }))
                    })
                }
            }
        } else {
            if (exists) {
                nextList = currentList.filter(id => id !== changedAssocId)
            }
        }

        nextList.sort((a, b) => a - b)
        this.callOnChangeGeneralMeetingFormDataSet({
            selectedAssocIdList: nextList,
            proposalFormDataSetList: proposalList,
        })
    }

    changeGeneralMeetingDate(generalMeetingDate: Nullable<Date>): void {
        this.callOnChangeGeneralMeetingFormDataSet({generalMeetingDate})
    }

    changeStartHour(event: SelectChangeEvent<unknown>): void {
        this.callOnChangeGeneralMeetingFormDataSet({startHour: event.target.value as string})
    }

    changeStartMinute(event: SelectChangeEvent<unknown>): void {
        this.callOnChangeGeneralMeetingFormDataSet({startMinute: event.target.value as string})
    }

    changeVotingPreferenceDeadline(votingPreferenceDeadline: Nullable<Date>): void {
        this.callOnChangeGeneralMeetingFormDataSet({votingPreferenceDeadline})
    }

    changeQuestionAvailability(event: React.ChangeEvent<{ value: string }>): void {
        const availability = event.target.value
        const shouldClearQuestionDeadline =
            availability === AVAILABLE_OR_NOT.UNAVAILABLE.value &&
            this.props.generalMeetingFormDataSet.questionDeadline !== null

        this.callOnChangeGeneralMeetingFormDataSet({
            questionAvailability: availability,
            ...(shouldClearQuestionDeadline ? {questionDeadline: null} : {})
        })
    }

    changeQuestionDeadline(questionDeadline: Nullable<Date>): void {
        this.callOnChangeGeneralMeetingFormDataSet({questionDeadline})
    }

    handleSelectFile(): void {
        const fileUpload = document.getElementById('fileUpload') as HTMLInputElement
        fileUpload.value = ''
        if (fileUpload) {
            fileUpload.click()
        }
    }

    async handleFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.props.eventEmitter.emit('clear:generalMeetingFile')

        const files = event.target.files
        if (!files || files.length === 0) return

        const file = files[0]
        const errors: string[] = [
            ...AdminGeneralMeetingInputForm.checkFileExtension(file),
            ...AdminGeneralMeetingInputForm.checkFileSize(file),
            ...AdminGeneralMeetingInputForm.checkTotalFileSize(
                file,
                this.props.generalMeetingFormDataSet.generalMeetingFileList
            ),
            ...await this.checkFileName(file),
        ]

        if (errors.length > 0) {
            const fileSizeMB = file.size === 0 ? 0 : Math.ceil(file.size / (1024 * 1024))
            const allMessages = [...errors, `${file.name}(${fileSizeMB}MB)`]
            allMessages.forEach(message => {
                this.props.eventEmitter.emit(
                    'generalMeetingFile',
                    new RequestItemError(
                        'generalMeetingFile',
                        message,
                        '',
                    )
                )
            })
            return
        }

        const reader = new FileReader()
        reader.onload = () => {
            const newFile: GeneralMeetingFile = {
                fileNumber: null,
                fileName: file.name,
                fileData: String(reader.result),
                fileSize: file.size
            }
            this.changeGeneralMeetingFile(newFile)
        }
        reader.readAsDataURL(file)
    }

    changeGeneralMeetingFile(newFile: GeneralMeetingFile): void {
        const currentFileList = this.props.generalMeetingFormDataSet.generalMeetingFileList
        this.callOnChangeGeneralMeetingFormDataSet({
            generalMeetingFileList: [...currentFileList, newFile]
        })
    }

    handleDeleteGeneralMeetingFile(index: number): void {
        const currentFileList = this.props.generalMeetingFormDataSet.generalMeetingFileList
        const deletedFile = currentFileList[index]
        if (deletedFile.fileNumber !== null && this.props.onDeleteExistingFile) {
            this.props.onDeleteExistingFile(deletedFile.fileNumber)
        }
        const nextFileList = currentFileList.filter((_, i) => i !== index)
        this.callOnChangeGeneralMeetingFormDataSet({
            generalMeetingFileList: nextFileList
        })
    }

    changeResolutionType(
        event: React.ChangeEvent<{ value: string }>,
        assocId: number,
        proposalNumber: number
    ): void {
        const {generalMeetingFormDataSet} = this.props
        const currentList = generalMeetingFormDataSet.proposalFormDataSetList

        const assocIndex = currentList.findIndex(x => x.assocId === assocId)
        if (assocIndex === -1) return

        const targetByAssoc = currentList[assocIndex]
        const detailIndex = targetByAssoc.proposalDetailInfoFormDataSetList
            .findIndex(d => d.proposalNumber === proposalNumber)
        if (detailIndex === -1) return

        const nextDetailList = [...targetByAssoc.proposalDetailInfoFormDataSetList]
        const currentDetail = nextDetailList[detailIndex]

        nextDetailList[detailIndex] = {...currentDetail, resolutionType: event.target.value}

        const nextByAssoc = {...targetByAssoc, proposalDetailInfoFormDataSetList: nextDetailList,}

        const nextByAssocList = [...currentList]
        nextByAssocList[assocIndex] = nextByAssoc

        this.callOnChangeGeneralMeetingFormDataSet({
            proposalFormDataSetList: nextByAssocList,
        })
    }

    changeProposalName(
        event: React.ChangeEvent<{ value: string }>,
        assocId: number,
        proposalNumber: number
    ): void {
        const currentList = this.props.generalMeetingFormDataSet.proposalFormDataSetList

        const assocIndex = currentList.findIndex(x => x.assocId === assocId)
        if (assocIndex === -1) return

        const targetByAssoc = currentList[assocIndex]
        const detailIndex = targetByAssoc.proposalDetailInfoFormDataSetList
            .findIndex(d => d.proposalNumber === proposalNumber)
        if (detailIndex === -1) return

        const nextDetailList = [...targetByAssoc.proposalDetailInfoFormDataSetList]
        const currentDetail = nextDetailList[detailIndex]

        nextDetailList[detailIndex] = {...currentDetail, proposalName: event.target.value}

        const nextByAssoc = {...targetByAssoc, proposalDetailInfoFormDataSetList: nextDetailList,}
        const nextByAssocList = [...currentList]
        nextByAssocList[assocIndex] = nextByAssoc

        this.callOnChangeGeneralMeetingFormDataSet({proposalFormDataSetList: nextByAssocList})
    }

    private swapProposal(assocId: number, proposalNumber: number, targetIndexOffset: number): void {
        const currentProposalListByAssoc = this.props.generalMeetingFormDataSet.proposalFormDataSetList

        const assocIndex = currentProposalListByAssoc.findIndex(a => a.assocId === assocId)
        if (assocIndex < 0) return

        const proposalListOfTargetAssoc = currentProposalListByAssoc[assocIndex]
        const proposalDetailInfoList = proposalListOfTargetAssoc.proposalDetailInfoFormDataSetList

        const index = proposalDetailInfoList.findIndex(p => p.proposalNumber === proposalNumber)
        const swapIndex = index + targetIndexOffset

        if (index < 0 || swapIndex < 0 || swapIndex >= proposalDetailInfoList.length) return

        const nextList = proposalDetailInfoList.slice()
        const temp = nextList[index]
        nextList[index] = nextList[swapIndex]
        nextList[swapIndex] = temp

        const renumbered = nextList.map((p, i) => ({...p, proposalNumber: i + 1}))
        const nextAssoc = {...proposalListOfTargetAssoc, proposalDetailInfoFormDataSetList: renumbered}
        const nextByAssocList = currentProposalListByAssoc.slice()
        nextByAssocList[assocIndex] = nextAssoc

        this.callOnChangeGeneralMeetingFormDataSet({proposalFormDataSetList: nextByAssocList})
    }

    handleUpProposal(assocId: number, proposalNumber: number): void {
        this.swapProposal(assocId, proposalNumber, -1)
    }

    handleDownProposal(assocId: number, proposalNumber: number): void {
        this.swapProposal(assocId, proposalNumber, 1)
    }

    handleDeleteProposal(assocId: number, proposalNumber: number): void {
        const currentProposalListByAssoc = this.props.generalMeetingFormDataSet.proposalFormDataSetList

        const assocIndex = currentProposalListByAssoc.findIndex(a => a.assocId === assocId)
        if (assocIndex < 0) return

        const proposalListOfTargetAssoc = currentProposalListByAssoc[assocIndex]
        const proposalDetailInfoList = proposalListOfTargetAssoc.proposalDetailInfoFormDataSetList

        if (proposalDetailInfoList.length <= 1) return

        const index = proposalDetailInfoList.findIndex(p => p.proposalNumber === proposalNumber)
        if (index < 0) return

        const nextList = proposalDetailInfoList.filter(p => p.proposalNumber !== proposalNumber)
        const renumbered = nextList.map((p, i) => ({...p, proposalNumber: i + 1}))
        const nextAssoc = {...proposalListOfTargetAssoc, proposalDetailInfoFormDataSetList: renumbered,}
        const nextByAssocList = currentProposalListByAssoc.slice()
        nextByAssocList[assocIndex] = nextAssoc

        this.callOnChangeGeneralMeetingFormDataSet({proposalFormDataSetList: nextByAssocList})
    }

    handleAddProposal(assocId: number): void {
        const currentProposalListByAssoc = this.props.generalMeetingFormDataSet.proposalFormDataSetList

        const assocIndex = currentProposalListByAssoc.findIndex(a => a.assocId === assocId)
        if (assocIndex < 0) return

        const proposalListOfTargetAssoc = currentProposalListByAssoc[assocIndex]
        const proposalDetailInfoList = proposalListOfTargetAssoc.proposalDetailInfoFormDataSetList ?? []

        const newItem = {
            proposalUID: createUUID(),
            proposalNumber: proposalDetailInfoList.length + 1,
            resolutionType: null,
            proposalName: '',
        }

        const nextList = proposalDetailInfoList.concat(newItem)
        const renumbered = nextList.map((p, i) => ({...p, proposalNumber: i + 1}))
        const nextAssoc = {...proposalListOfTargetAssoc, proposalDetailInfoFormDataSetList: renumbered,}
        const nextByAssocList = currentProposalListByAssoc.slice()
        nextByAssocList[assocIndex] = nextAssoc

        this.callOnChangeGeneralMeetingFormDataSet({proposalFormDataSetList: nextByAssocList})
    }

    changeNoticeContent(event: React.ChangeEvent<HTMLInputElement>): void {
        this.callOnChangeGeneralMeetingFormDataSet({noticeContent: event.target.value})
    }

    changeReleaseDate(releaseDate: Nullable<Date>): void {
        this.callOnChangeGeneralMeetingFormDataSet({releaseDate})
    }

    renderAssocCheckBox(
        assocList: AssocOfCondominium[], selectedAssocIdList: number[], isAfterRelease: boolean
    ): ReactNode {
        return <AssocLeftRowFlexContainer>
            {assocList.map((assoc) =>
                <PPCheckboxGroup
                    key={assoc.assocId}
                    data={[{
                        label: assoc.assocName,
                        checked: selectedAssocIdList.includes(assoc.assocId),
                        checkboxProps: {
                            disabled: assocList.length === 1 || isAfterRelease,
                        },
                    }]}
                    onChangeChecked={this.changeAssoc}
                />
            )}
        </AssocLeftRowFlexContainer>
    }

    renderGeneralMeetingFile(file: GeneralMeetingFile, index: number): ReactNode {
        return (
            <FullWidthLeftColumnFlexContainer key={`file-${index}`}>
                <Box height={'14px'}/>
                <Divider $color={Colors.AccentLight}/>
                <Box height={'14px'}/>
                <FullWidthRowFlexContainer>
                    <LeftRowFlexContainer>
                        <StyledLink href={String(file.fileData)} download={file.fileName}>
                            <AttachmentFileStyle>
                                {getLinkIconComponent(getFileExtension(file.fileName))}
                            </AttachmentFileStyle>
                            <AttachmentFIleNameText>
                                {file.fileName}
                            </AttachmentFIleNameText>
                        </StyledLink>
                        <Box width={'8px'}/>
                        <FileSizeText>
                            ({formatFileSize(file.fileSize)})
                        </FileSizeText>
                    </LeftRowFlexContainer>
                    <NoShrinkDeleteLink
                        onClick={(event: React.MouseEvent) => {
                            event.preventDefault()
                            this.handleDeleteGeneralMeetingFile(index)
                        }}
                    >
                        削除
                    </NoShrinkDeleteLink>
                </FullWidthRowFlexContainer>
            </FullWidthLeftColumnFlexContainer>
        )
    }

    renderProposalListByAssoc(assocId: number): ReactNode {
        const targetAssoc = this.props.assocList.find(assoc => assoc.assocId === assocId)
        const targetProposalList = this.props.generalMeetingFormDataSet.proposalFormDataSetList
            .find(proposalList => proposalList.assocId === assocId)

        if (targetAssoc === null || targetProposalList === undefined) return null

        const assocIndex = this.props.generalMeetingFormDataSet.proposalFormDataSetList.findIndex(
            item => item.assocId === assocId
        )

        const numberOfProposal = targetProposalList.proposalDetailInfoFormDataSetList.length

        const overLimitOfProposalCount: boolean = numberOfProposal >= HKConstants.VMN.GMT.MAX_NUMBER_OF_PROPOSAL_COUNT

        return (
            <PageMainArea key={assocId}>
                <Table>
                    <colgroup>
                        <col style={{width: 150}}/>
                        <col/>
                        <col style={{width: 168}}/>
                    </colgroup>
                    <StyledTableCaption>
                        議案 <AssocNameText>{targetAssoc?.assocName}</AssocNameText>
                    </StyledTableCaption>
                    <TableBody>
                        {targetProposalList.proposalDetailInfoFormDataSetList.map(
                            ((targetProposal, index) => this.renderProposal(
                                assocId,
                                targetProposal,
                                assocIndex,
                                index,
                                numberOfProposal
                            ))
                        )}
                        <TableRow>
                            <TableCell colSpan={4} style={{paddingTop: 0, paddingBottom: 0}}>
                                <AddProposal>
                                    <CustomStyledLink
                                        onClick={() => this.handleAddProposal(assocId)}
                                        disabled={overLimitOfProposalCount || this.props.isAfterRelease}
                                    >
                                        <AddCircle/>
                                        <AddProposalText>
                                            議案を追加する
                                        </AddProposalText>
                                    </CustomStyledLink>
                                    {overLimitOfProposalCount &&
                                        <AlertOverLimitOfProposalCountMessage>
                                            議案の上限に達しています。削除してから再度追加を押してください。
                                        </AlertOverLimitOfProposalCountMessage>
                                    }
                                </AddProposal>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </PageMainArea>
        )
    }

    renderProposal(
        assocId: number,
        proposal: ProposalDetailInfoFormDataSet,
        assocIndex: number,
        proposalIndex: number,
        countOfProposal: number
    ): ReactNode {
        return <TableRow key={proposal.proposalUID}>
            <TableRowName required={proposal.proposalNumber === 1} optional={proposal.proposalNumber !== 1}>
                <ProposalNumberText>
                    第{proposal.proposalNumber}号議案
                </ProposalNumberText>
            </TableRowName>
            <TableCell>
                <LeftColumnFlexContainer>
                    <StyledLeftRowFlexContainer>
                        <DisabledLabel disabled={this.props.isAfterRelease}>決議の種類</DisabledLabel>
                        <Box width={'2px'}/>
                        <RadioGroup
                            value={proposal.resolutionType}
                            onChange={(event) => this.changeResolutionType(event, assocId, proposal.proposalNumber)}
                        >
                            <LeftRowFlexContainer>
                                <MediumSizeFormControlLabel
                                    value={RESOLUTION_TYPE.ORDINARY.value}
                                    control={<Radio color={'primary'} disabled={this.props.isAfterRelease}/>}
                                    label={RESOLUTION_TYPE.ORDINARY.label}
                                />
                                <LastMediumSizeFormControlLabel
                                    value={RESOLUTION_TYPE.SPECIAL.value}
                                    control={<Radio color={'primary'} disabled={this.props.isAfterRelease}/>}
                                    label={RESOLUTION_TYPE.SPECIAL.label}
                                />
                            </LeftRowFlexContainer>
                        </RadioGroup>
                        <Box width={'12px'}/>
                        <DisabledLabel disabled={this.props.isAfterRelease}>議案名</DisabledLabel>
                        <div style={{flex: 1, minWidth: 0}}>
                            <PPTextField
                                InputProps={{
                                    inputProps: {
                                        maxLength: 100,
                                    },
                                }}
                                value={proposal.proposalName}
                                onChange={
                                    (event) => this.changeProposalName(event, assocId, proposal.proposalNumber)
                                }
                                placeholder={`例：〇〇を□□する件について`}
                                eventKeys={[`proposalList[${assocIndex}].proposalDetailInfoList[${proposalIndex}].proposalName`]}
                                eventEmitter={this.props.eventEmitter}
                                disabled={this.props.isAfterRelease}
                                fullWidth
                                sx={{
                                    '& .MuiInputBase-input.Mui-disabled': {
                                        backgroundColor: '#DDD',
                                    },
                                }}
                            />
                        </div>
                    </StyledLeftRowFlexContainer>
                    <PPErrorLabel
                        eventEmitter={this.props.eventEmitter}
                        eventKeys={[`assoc[${assocIndex}].proposal[${proposalIndex}]`]}
                    />
                </LeftColumnFlexContainer>
            </TableCell>
            <TableCell>
                <FullWidthLeftRowFlexContainer>
                    <ArrowIconArea>
                        <IconButton
                            aria-label="上の議案と入れ替え"
                            onClick={() => this.handleUpProposal(assocId, proposal.proposalNumber)}
                            size="medium"
                            disabled={this.props.isAfterRelease || proposalIndex < 1}
                        >
                            <ArrowUpward fontSize="medium"/>
                        </IconButton>
                        <IconButton
                            aria-label="下の議案と入れ替え"
                            onClick={() => this.handleDownProposal(assocId, proposal.proposalNumber)}
                            size="medium"
                            disabled={this.props.isAfterRelease || countOfProposal <= proposalIndex + 1}
                        >
                            <ArrowDownward fontSize="medium"/>
                        </IconButton>
                    </ArrowIconArea>
                    <DeleteLink
                        onClick={() => this.handleDeleteProposal(assocId, proposal.proposalNumber)}
                        disabled={this.props.isAfterRelease || countOfProposal <= 1}
                    >
                        削除
                    </DeleteLink>
                </FullWidthLeftRowFlexContainer>
            </TableCell>
        </TableRow>
    }

    render(): ReactNode {
        const {
            assocList,
            generalMeetingFormDataSet,
            currentDate,
            eventEmitter,
            isAfterRelease,
        } = this.props

        const tomorrow
            = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1)

        return <InputArea>
            <PageMainArea>
                <Table>
                    <colgroup>
                        <col style={{width: 250}}/>
                        <col/>
                    </colgroup>
                    <StyledTableCaption>開催要項</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName required>総会名</TableRowName>
                            <TableCell>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: 100,
                                        },
                                    }}
                                    value={generalMeetingFormDataSet.generalMeetingName}
                                    autoFocus
                                    onChange={this.changeGeneralMeetingName}
                                    eventKeys={['generalMeetingName']}
                                    eventEmitter={eventEmitter}
                                    fullWidth
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName required>管理組合</TableRowName>
                            <TableCell>
                                {this.renderAssocCheckBox(
                                    assocList, generalMeetingFormDataSet.selectedAssocIdList, isAfterRelease
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName required>開催日</TableRowName>
                            <TableCell>
                                <Box width={'200px'}>
                                    <PPDatePicker
                                        value={generalMeetingFormDataSet.generalMeetingDate}
                                        onChangeDate={this.changeGeneralMeetingDate}
                                        disabled={isAfterRelease}
                                        eventKeys={['generalMeetingDate']}
                                        eventEmitter={eventEmitter}
                                    />
                                </Box>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableRowName
                                hint={Messages.HGMTA0001.toString()}
                                required
                            >
                                開始時間
                            </TableRowName>
                            <TableCell>
                                <StartTimeLeftRowFlexContainer>
                                    <div>
                                        <PPSelect
                                            data={addEmptyData(HOURS.values())}
                                            value={generalMeetingFormDataSet.startHour}
                                            onChange={this.changeStartHour}
                                            disabled={isAfterRelease}
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    backgroundColor: '#DDD',
                                                },
                                            }}
                                        />
                                    </div>
                                    <div>：</div>
                                    <div>
                                        <PPSelect
                                            data={addEmptyData(RESERVATION_TIME_UNIT_MINUTES.values())}
                                            value={generalMeetingFormDataSet.startMinute}
                                            onChange={this.changeStartMinute}
                                            disabled={isAfterRelease}
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    backgroundColor: '#DDD',
                                                },
                                            }}
                                        />
                                    </div>
                                    <div>〜</div>
                                </StartTimeLeftRowFlexContainer>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                <Table>
                    <colgroup>
                        <col style={{width: 250}}/>
                        <col/>
                    </colgroup>
                    <StyledTableCaption>意思表明の受付</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName
                                hint={Messages.HGMTA0002.toString()}
                                required
                            >
                                案内上の期限
                            </TableRowName>
                            <TableCell>
                                <Box width={'200px'}>
                                    <PPDatePicker
                                        value={generalMeetingFormDataSet.votingPreferenceDeadline}
                                        onChangeDate={this.changeVotingPreferenceDeadline}
                                        eventKeys={['votingPreferenceDeadline']}
                                        eventEmitter={eventEmitter}
                                    />
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                <Table>
                    <colgroup>
                        <col style={{width: 250}}/>
                        <col/>
                    </colgroup>
                    <StyledTableCaption>質問の受付</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName
                                hint={Messages.HGMTA0003.toString()}
                                required
                            >
                                受付可否
                            </TableRowName>
                            <TableCell>
                                <LeftRowFlexContainer>
                                    <RadioGroup
                                        value={generalMeetingFormDataSet.questionAvailability}
                                        onChange={this.changeQuestionAvailability}
                                    >
                                        <LeftRowFlexContainer>
                                            <MediumSizeFormControlLabel
                                                value={AVAILABLE_OR_NOT.UNAVAILABLE.value}
                                                control={<Radio color={'primary'} disabled={isAfterRelease}/>}
                                                label={'質問を受け付けない'}
                                            />
                                            <MediumSizeFormControlLabel
                                                value={AVAILABLE_OR_NOT.AVAILABLE.value}
                                                control={<Radio color={'primary'} disabled={isAfterRelease}/>}
                                                label={'質問を受け付ける'}
                                            />
                                        </LeftRowFlexContainer>
                                    </RadioGroup>
                                    <QuestionDeadlineLabel disabled={
                                        isAfterRelease ||
                                        generalMeetingFormDataSet.questionAvailability
                                        !== AVAILABLE_OR_NOT.AVAILABLE.value
                                    }>
                                        締切日
                                    </QuestionDeadlineLabel>
                                    <Box width={'200px'}>
                                        <PPDatePicker
                                            value={generalMeetingFormDataSet.questionDeadline}
                                            onChangeDate={this.changeQuestionDeadline}
                                            disabled={
                                                isAfterRelease ||
                                                generalMeetingFormDataSet.questionAvailability
                                                !== AVAILABLE_OR_NOT.AVAILABLE.value
                                            }
                                            eventKeys={['questionDeadline']}
                                            eventEmitter={eventEmitter}
                                        />
                                    </Box>
                                </LeftRowFlexContainer>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                <Table>
                    <colgroup>
                        <col style={{width: 250}}/>
                        <col/>
                    </colgroup>
                    <StyledTableCaption>資料</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName required>掲載資料</TableRowName>
                            <TableCell>
                                <LeftRowFlexContainer>
                                    <AdminDefaultButton
                                        onClick={() => this.handleSelectFile()}
                                        disabled={
                                            generalMeetingFormDataSet.generalMeetingFileList.length
                                            >= HKConstants.VMN.GMT.MAX_TOTAL_FILE_COUNT
                                        }
                                    >
                                        PDFを選択
                                    </AdminDefaultButton>
                                    <DisplayNoneInput
                                        type={'file'}
                                        id={'fileUpload'}
                                        accept={'application/pdf'}
                                        onChange={(e) => this.handleFile(e)}
                                    />
                                    <Box ml={'16px'}>
                                        1ファイル50MB、累計100MBまで
                                    </Box>
                                </LeftRowFlexContainer>
                                <PPErrorLabel
                                    eventEmitter={this.props.eventEmitter}
                                    eventKeys={['generalMeetingFile']}
                                />
                                {generalMeetingFormDataSet.generalMeetingFileList.map(
                                    (file, index) => this.renderGeneralMeetingFile(file, index)
                                )}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </PageMainArea>
            {generalMeetingFormDataSet.selectedAssocIdList.length === 0
                ? <PageMainArea>
                    <Table>
                        <colgroup>
                            <col style={{width: 250}}/>
                            <col/>
                        </colgroup>
                        <StyledTableCaption>議案</StyledTableCaption>
                        <TableBody>
                            <TableRow>
                                <TableRowName>管理組合を選択後に入力できます</TableRowName>
                            </TableRow>
                        </TableBody>
                    </Table>
                </PageMainArea>
                : assocList
                    .filter(assoc => generalMeetingFormDataSet.selectedAssocIdList.includes(assoc.assocId))
                    .map(assoc => this.renderProposalListByAssoc(assoc.assocId))
            }
            <PageMainArea>
                <Table>
                    <colgroup>
                        <col style={{width: 250}}/>
                        <col/>
                    </colgroup>
                    <StyledTableCaption>特記事項</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <NoticeContentTitle>
                                    連絡内容
                                    <Box ml={'100px'}><OptionalChip label={'任意'}/></Box>
                                </NoticeContentTitle>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: 500,
                                        },
                                    }}
                                    minRows={5}
                                    value={generalMeetingFormDataSet.noticeContent}
                                    fullWidth
                                    multiline
                                    onChange={this.changeNoticeContent}
                                    placeholder={'資料の差し替え・追加などの情報を記入することができます。'}
                                    eventKeys={['noticeContent']}
                                    eventEmitter={eventEmitter}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </PageMainArea>
            <PageMainArea>
                <Table>
                    <colgroup>
                        <col style={{width: 250}}/>
                        <col/>
                    </colgroup>
                    <StyledTableCaption>公開設定</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName required>公開開始日</TableRowName>
                            <TableCell>
                                <Box width={'200px'}>
                                    <PPDatePicker
                                        value={generalMeetingFormDataSet.releaseDate}
                                        onChangeDate={this.changeReleaseDate}
                                        minDate={tomorrow}
                                        disabled={isAfterRelease}
                                        eventKeys={['releaseDate']}
                                        eventEmitter={eventEmitter}
                                    />
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </PageMainArea>
        </InputArea>
    }
}

const InputArea = styled.div`
  & > * + * {
    margin-top: 24px;
  }
`

const AssocLeftRowFlexContainer = styled(LeftRowFlexWrapContainer)`
  flex: 1 1 0;

  .MuiFormControlLabel-label {
    overflow-wrap: anywhere;
  }
`

const QuestionDeadlineLabel = styled.div<{ disabled: boolean }>`
  padding-right: 12px;
  color: ${({disabled}) => disabled ? 'rgba(0, 0, 0, 0.38)' : 'inherit'};
`

const DisabledLabel = styled.span<{ disabled: boolean }>`
  color: ${({disabled}) => disabled ? 'rgba(0, 0, 0, 0.38)' : 'inherit'};
`

const DisplayNoneInput = styled.input`
  display: none;
`

const FileSizeText = styled.div`
  font-size: ${FontSizeInPixel.Small}px;
  color: rgba(0, 0, 0, 0.7);
`

const NoShrinkDeleteLink = styled(DeleteLink)`
  flex-shrink: 0;
  padding-left: 4px;
`

const StyledLeftRowFlexContainer = styled(FullWidthLeftRowFlexContainer)`
  gap: 6px;
`

const StartTimeLeftRowFlexContainer = styled(LeftRowFlexContainer)`
  gap: 5px;
`

const ProposalNumberText = styled.div`
  font-size: ${FontSizeInPixel.Body}px;
  font-weight: bold;
  padding-left: 12px;
`

const AddProposal = styled(LeftRowFlexContainer)`
  padding: 8px;
  height: 48px;
`

const AddProposalText = styled.div`
  padding-left: 8px;
`

const AlertOverLimitOfProposalCountMessage = styled.div`
  padding-left: 8px;
`

const MediumSizeFormControlLabel = styled(FormControlLabel)`
  .MuiFormControlLabel-label {
    font-size: 1em;
  }
`

const LastMediumSizeFormControlLabel = styled(FormControlLabel)`
  margin-right: 8px;

  .MuiFormControlLabel-label {
    font-size: 1em;
  }
`

const ArrowIconArea = styled(LeftRowFlexContainer)`
  padding-right: 12px;
`

const NoticeContentTitle = styled(LeftRowFlexContainer)`
  margin-bottom: 16px;
  width: 226px;
  justify-content: space-between;
`

const AssocNameText = styled.span`
  font-weight: bold;
  line-break: anywhere;
`

const AttachmentFIleNameText = styled.span`
  line-break: anywhere;
`
